import { ProductNames } from '@/components/providers/ApplicantContextProvider/ApplicantContextProvider.types';
import { EVENT_LOG_NAMES, honeybadgerLogEvent } from '@/components/shared/utils/logging';
import { emDash, normalizeInternalEmail } from '@/components/shared/utils/utils';

import { Condition } from './ApplicantConditions.types';

export { getFileTypeLabel } from '@/components/shared/utils/utils';

/** Prefilled (editable) resolution note when accepting an automated resolution. */
export const AUTO_RESOLVE_NOTE_TEMPLATE =
    '**[AutoResolved]**\nResolution is based on the information that was automatically provided.';

const PRODUCT_LABELS: { product: ProductNames; label: string }[] = [
    { product: ProductNames.FACTORING, label: 'Factoring' },
    { product: ProductNames.LOADPAY, label: 'LoadPay' },
    { product: ProductNames.TCSFUEL, label: 'Fuel' },
];

interface MarkdownNode {
    type: string;
    value?: string;
    children?: MarkdownNode[];
}

const WHITESPACE = /[\s\u200B\uFEFF]+/g;
const LINE_END = /\r\n?|\n/;
const VISIBLE_TEXT_TYPES = ['text', 'inlineCode', 'html'];

const splitLines = (nodes: MarkdownNode[]): MarkdownNode[][] => {
    const lines: MarkdownNode[][] = [[]];
    const addToLine = (node: MarkdownNode, startsNewLine: boolean) => {
        if (startsNewLine) lines.push([]);
        lines[lines.length - 1].push(node);
    };
    for (const node of nodes) {
        if (node.type === 'break') {
            lines.push([]);
        } else if (node.children) {
            splitLines(node.children).forEach((children, i) =>
                addToLine({ ...node, children }, i > 0),
            );
        } else if (node.type !== 'inlineCode' && node.value) {
            node.value.split(LINE_END).forEach((value, i) => addToLine({ ...node, value }, i > 0));
        } else {
            addToLine(node, false);
        }
    }
    return lines;
};

const getRawText = (nodes: MarkdownNode[]): string =>
    nodes
        .map((node) =>
            VISIBLE_TEXT_TYPES.includes(node.type)
                ? (node.value ?? '')
                : getRawText(node.children ?? []),
        )
        .join('');

const getLineText = (nodes: MarkdownNode[]): string =>
    getRawText(nodes).replace(WHITESPACE, ' ').trim();

const findTitle = (node: MarkdownNode): string => {
    if (node.type === 'paragraph' || node.type === 'heading' || node.type === 'code') {
        const children =
            node.type === 'code' ? [{ type: 'text', value: node.value }] : node.children;
        return (
            splitLines(children ?? [])
                .map(getLineText)
                .find(Boolean) ?? ''
        );
    }
    for (const child of node.children ?? []) {
        const title = findTitle(child);
        if (title) return title;
    }
    return '';
};

// List and quote markers sit outside the paragraph node, so they drop out of the title.
export const firstLineAsTitle = () => (root: MarkdownNode) => {
    const title = findTitle(root);
    root.children = title
        ? [{ type: 'paragraph', children: [{ type: 'text', value: ` to ${title}` }] }]
        : [];
};

export const getProductScopeLabel = (products: ProductNames[]): string =>
    PRODUCT_LABELS.filter(({ product }) => products.includes(product))
        .map(({ label }) => label)
        .join(' + ');

export const getProductLabel = (product: ProductNames): string =>
    PRODUCT_LABELS.find((p) => p.product === product)?.label ?? product;

// Underwriting's fixed sort order (ORIG-1191), used for both open and closed/resolved
// conditions -- closed no longer sorts by dateResolved. Ties (the unranked tail) fall
// back to oldest-created-first.
export const sortByFixedRank = (conditions: Condition[]): Condition[] =>
    [...conditions].sort((a, b) => {
        if (a.sortRank !== b.sortRank) {
            return a.sortRank - b.sortRank;
        }
        return new Date(a.dateCreated).getTime() - new Date(b.dateCreated).getTime();
    });

const sortByIsoDesc = <T>(items: T[], getDate: (item: T) => string | null | undefined): T[] =>
    [...items].sort((a, b) => {
        const aTime = getDate(a) ? new Date(getDate(a) as string).getTime() : 0;
        const bTime = getDate(b) ? new Date(getDate(b) as string).getTime() : 0;
        return bTime - aTime;
    });

export type CreatorGroupKey = 'mine' | 'others' | 'system';

export interface CreatorGroup {
    key: CreatorGroupKey;
    title: string;
    conditions: Condition[];
}

const isSameEmail = (a: string, b: string) =>
    normalizeInternalEmail(a.trim()).toLowerCase() ===
    normalizeInternalEmail(b.trim()).toLowerCase();

// ORIG-1359. Expects fixed-rank input: System keeps that order, Me and Others re-sort newest-first.
export const groupByCreator = (
    conditions: Condition[],
    currentUserEmail: string | undefined,
): CreatorGroup[] => {
    const system = conditions.filter((c) => isSystemUser(c.createdBy));
    const people = conditions.filter((c) => !isSystemUser(c.createdBy));
    const isMine = (c: Condition) =>
        Boolean(currentUserEmail) && isSameEmail(c.createdBy, currentUserEmail as string);
    return [
        {
            key: 'mine',
            title: 'Created by Me',
            conditions: sortByIsoDesc(people.filter(isMine), (c) => c.dateCreated),
        },
        {
            key: 'others',
            title: 'Created by Others',
            conditions: sortByIsoDesc(
                people.filter((c) => !isMine(c)),
                (c) => c.dateCreated,
            ),
        },
        { key: 'system', title: 'Created by Originations System', conditions: system },
    ];
};

export const sortDeletedConditions = (conditions: Condition[]): Condition[] =>
    sortByIsoDesc(conditions, (c) => c.dateDeleted);

export const getInitials = (name: string): string =>
    name
        // could be a name or an email
        .split(/[\s@.]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase())
        .join('') || '?';

const SYSTEM_USER_NAME = 'originationssystem';
const ORP_LOGO_SRC = '/favicon.png';

export type AvatarContent =
    | { type: 'image'; src: string; alt: string }
    | { type: 'initials'; initials: string };

export const isSystemUser = (name: string): boolean =>
    name.trim().toLowerCase() === SYSTEM_USER_NAME;

export const getAvatar = (name: string): AvatarContent =>
    isSystemUser(name)
        ? { type: 'image', src: ORP_LOGO_SRC, alt: 'ORP' }
        : { type: 'initials', initials: getInitials(name) };

export const formatDateShort = (iso: string): string => {
    if (!iso) {
        honeybadgerLogEvent(EVENT_LOG_NAMES.Exception, {
            level: 'WARN',
            customAttributes: { message: 'formatDateShort called with empty date' },
        });
        return emDash;
    }

    const d = new Date(iso);
    if (isNaN(d.getTime())) {
        honeybadgerLogEvent(EVENT_LOG_NAMES.Exception, {
            level: 'WARN',
            customAttributes: { message: 'formatDateShort called with invalid date', date: iso },
        });
        return emDash;
    }

    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const parseLoggedDate = (iso: string, caller: string): Date | null => {
    if (!iso) {
        honeybadgerLogEvent(EVENT_LOG_NAMES.Exception, {
            level: 'WARN',
            customAttributes: { message: `${caller} called with empty date` },
        });
        return null;
    }

    const d = new Date(iso);
    if (isNaN(d.getTime())) {
        honeybadgerLogEvent(EVENT_LOG_NAMES.Exception, {
            level: 'WARN',
            customAttributes: { message: `${caller} called with invalid date`, date: iso },
        });
        return null;
    }

    return d;
};

const formatClockTime = (d: Date): string =>
    d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

export const formatDateTime = (iso: string): string => {
    const d = parseLoggedDate(iso, 'formatDateTime');
    if (!d) return emDash;

    const date = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    return `${date}, ${formatClockTime(d)}`;
};

/** Time only, for notes grouped under a header that already shows the date. */
export const formatTime = (iso: string): string => {
    const d = parseLoggedDate(iso, 'formatTime');
    return d ? formatClockTime(d) : emDash;
};

export const getAppliesToProductOptions = (
    availableProducts: ProductNames[],
): { label: string; value: ProductNames }[] =>
    PRODUCT_LABELS.filter(({ product }) => availableProducts.includes(product)).map(
        ({ product, label }) => ({ label, value: product }),
    );
